(ns hai.web-app.auth
  "Common authentication functionality

  `init!` is called at boot time to fetch the current user information and store
  it in the app-db as `:auth/identity`. A number of subscriptions provide
  access.
  "
  (:require
   [clojure.string :as str]
   [hai.ui.rf-sugar :refer [<s >e >e! defevent defevent-fx defsub defeffect]]
   [hai.web-app.sync :as sync]
   [hai.web-app.util.fetch :as fetch]))

(defsub :auth/identity [db]
  (:auth/identity db))

(defsub :auth/logged-in? [db identity <- :auth/identity]
  (:logged-in? identity))

(defsub :auth/admin? [db identity <- :auth/identity]
  (:user/admin identity))

(defsub :auth/email [db identity <- :auth/identity]
  (-> identity :token/claims :email))

(defsub :auth/legacy? [db identity <- :auth/identity]
  (boolean (:user/legacy? identity)))

(defevent-fx :user/save-collection-view
  [{:keys [db]} collection-key view]
  {:db (assoc-in db [:auth/identity :user-config/collection-views collection-key] view)
   :resolve {:query `[(~'user/save-collection-view
                       ~{:collection/key collection-key :collection/view view})]}})

(defevent-fx :user/save-collection-setting
  [{:keys [db]} collection-key setting-key setting-val]
  (let [existing (get-in db [:auth/identity :user-config/collection-settings collection-key])
        new-settings (assoc (or existing {}) setting-key setting-val)]
    {:db (assoc-in db [:auth/identity :user-config/collection-settings collection-key] new-settings)
     :resolve {:query `[(~'user/save-collection-settings
                         ~{:collection/key collection-key :collection/settings new-settings})]}}))

(defevent-fx :auth/toggle-legacy
  [{:keys [db]}]
  (let [new-val (not (get-in db [:auth/identity :user/legacy?]))]
    {:db (assoc-in db [:auth/identity :user/legacy?] new-val)
     :resolve {:query `[(~'user/update
                         ~{:user/uuid (-> db :auth/identity :user/uuid)
                           :user/legacy? new-val})]}}))

;; Copy for the new-tab override setting, kept next to the event the settings
;; row dispatches. Home exposes the same setting as a step on the setup card,
;; under its own wording.
(def newtab-toggle-settings-label
  "Open Humble on new tabs (Recommended)")

;; Three things, in the order they matter to someone reading the row: what the
;; setting does, what they have to do to make it stick, and where it works.
;;
;; The middle sentence is the one that can't be dropped. On the first new tab
;; after switching this on, the browser asks whether to keep the change, and
;; choosing "Change it back" revokes the override — so a user who picks the
;; wrong button gets a setting that is on, says it is on, and does nothing.
;;
;; Edge has to be named: it is Chromium-based, so "Chromium browsers" alone
;; would imply it works, but Microsoft restricts new-tab replacement. Hence
;; "most other", not "other". Safari and Firefox are named because this page
;; loads fine in both, so someone can reach this toggle in a browser with no
;; extension to honour it.
(def newtab-toggle-description
  (str "Every new tab opens with Humble instead of a blank page. "
       "The first time you open a new tab, your browser will ask whether to keep the change — choose “Keep it”. "
       "Works in Chrome, Arc, Ecosia and most other Chromium browsers, but not Edge, Safari or Firefox."))

;; The enable toast repeats the instruction because it lands at the right
;; moment — the user has just switched this on and the browser's dialog is
;; seconds away, on their next new tab.
(def ^:private newtab-enabled-toast
  "New tabs will open with Humble — choose “Keep it” when your browser asks")

(def ^:private newtab-disabled-toast
  "New tabs will open as normal")

;; The extension injects a content script on this page; it hears this postMessage
;; and updates its local new-tab cache immediately, so the next new tab reflects
;; the toggle without waiting for the extension's own periodic sync. A no-op
;; (unheard) when the extension isn't installed.
(defeffect ::notify-extension-newtab-override [override?]
  (js/window.postMessage
   (clj->js {:humble_ext_event "newtab-override-changed"
             :override (boolean override?)})
   "*"))

(defevent-fx ::set-newtab-override
  [{:keys [db]} override?]
  {:db (assoc-in db [:auth/identity :user-config.newtab/override?] override?)
   :resolve {:query `[(~'user/update
                       ~{:user/uuid (-> db :auth/identity :user/uuid)
                         :user-config.newtab/override? override?})]}
   ::notify-extension-newtab-override override?
   ;; Confirms the change wherever it was made.
   :fx [[:dispatch [:toast/info
                    (if override?
                      ;; Longer than the default 5s: this one carries an
                      ;; instruction to act on, not a confirmation to glance at.
                      {:text newtab-enabled-toast :timeout 9000}
                      {:text newtab-disabled-toast})]]]})

;; Copy for the Humble bookmark setting: the peek-out button the extension puts
;; on the right edge of every page. Off for new users, so the description also
;; says where the same signal lives instead.
(def bookmark-toggle-label
  "Show the Humble bookmark on web pages")

(def bookmark-toggle-description
  (str "A small Humble tab sits on the right edge of every page and turns green when Humble has notes or tasks for it. "
       "Pin Humble to your toolbar to see the same colour there instead."))

;; Same bridge as the new-tab override. The key is `visible`, not `visible?`:
;; the content script reads it as a JS property.
(defeffect ::notify-extension-bookmark-visible [visible?]
  (js/window.postMessage
   (clj->js {:humble_ext_event "bookmark-visible-changed"
             :visible (boolean visible?)})
   "*"))

;; No toast: the content script runs on this page too, so the bookmark appears
;; or disappears right here as the switch flips.
(defevent-fx ::set-bookmark-visible
  [{:keys [db]} visible?]
  {:db (assoc-in db [:auth/identity :user-config.bookmark/visible?] visible?)
   :resolve {:query `[(~'user/update
                       ~{:user/uuid (-> db :auth/identity :user/uuid)
                         :user-config.bookmark/visible? visible?})]}
   ::notify-extension-bookmark-visible visible?})

(defevent-fx :auth/init [_]
  {:fetch {:url "/me"
           :store-in [:auth/identity]}})

(defevent-fx :auth/impersonate [_ user-uuid]
  {:fetch {:url "/impersonate"
           :method :post
           :body {:user/uuid user-uuid}
           :on-success-fx [[:dispatch [:auth/init]]
                           [:dispatch [:routing/navigate [:admin-ui/profile]]]]}})

(defeffect :auth/perform-logout
  [_]
  (.then (js/fetch "/logout" (clj->js {:method "GET"
                                       :headers fetch/build-headers}))
         (fn [_] (set! js/window.location "/app"))))

(defevent-fx :auth/logout
  [_]
  {:auth/perform-logout nil})

(defn init! []
  (>e! :auth/init)
  (>e! [::sync/watch {:owner ::identity
                      :scope :user
                      :refetch [[:auth/init]]}]))
